import { useCallback, useEffect, useRef, useState } from 'react';

import { notification } from 'antd';
import { useIntl } from 'react-intl';

export const COPY_TO_CLIPBOARD_FEEDBACK_MS = 2000;

export interface UseCopyToClipboardOptions {
  /** i18n id for the clipboard error notification. */
  errorMessageId?: string;
  /** How long `copied` stays true after a successful copy. */
  feedbackMs?: number;
  /** When this value changes, clear the copied feedback state. */
  resetKey?: string | number | null;
}

export interface UseCopyToClipboardResult {
  copied: boolean;
  copy: (text: string) => Promise<boolean>;
}

export const useCopyToClipboard = (
  options: UseCopyToClipboardOptions = {},
): UseCopyToClipboardResult => {
  const intl = useIntl();
  const {
    errorMessageId = 'copyLink.error',
    feedbackMs = COPY_TO_CLIPBOARD_FEEDBACK_MS,
    resetKey,
  } = options;

  const [copied, setCopied] = useState(false);
  const copiedResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCopiedFeedback = useCallback(() => {
    setCopied(false);
    if (copiedResetTimeoutRef.current) {
      clearTimeout(copiedResetTimeoutRef.current);
      copiedResetTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => clearCopiedFeedback(), [clearCopiedFeedback]);

  useEffect(() => {
    clearCopiedFeedback();
  }, [clearCopiedFeedback, resetKey]);

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        if (copiedResetTimeoutRef.current) {
          clearTimeout(copiedResetTimeoutRef.current);
        }
        copiedResetTimeoutRef.current = setTimeout(() => {
          setCopied(false);
          copiedResetTimeoutRef.current = null;
        }, feedbackMs);
        return true;
      } catch {
        clearCopiedFeedback();
        notification.error({
          message: intl.formatMessage({ id: errorMessageId }),
          placement: 'topRight',
        });
        return false;
      }
    },
    [clearCopiedFeedback, errorMessageId, feedbackMs, intl],
  );

  return { copied, copy };
};
